import React, { useEffect, useRef } from 'react';
import * as echarts from 'echarts';

const DoctorPerformanceChart = ({ data }) => {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  useEffect(() => {
    if (!chartRef.current || !data || data.length === 0) return;

    // Initialize chart
    if (!chartInstance.current) {
      chartInstance.current = echarts.init(chartRef.current);
    }

    const doctorNames = data.map(item => item.doctor_name || 'Unknown');
    const revenues = data.map(item => parseFloat(item.revenue || 0));
    const patients = data.map(item => parseInt(item.patients || 0));

    const option = {
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow'
        },
        formatter: (params) => {
          let result = `<div style="font-weight: 600; margin-bottom: 8px;">${params[0].axisValue}</div>`;
          params.forEach(param => {
            const value = param.seriesName === 'Revenue' 
              ? `QR ${param.value.toLocaleString()}`
              : `${param.value} patients`;
            result += `
              <div style="display: flex; align-items: center; margin-bottom: 4px;">
                <span style="display: inline-block; width: 12px; height: 12px; background: ${param.color}; border-radius: 2px; margin-right: 8px;"></span>
                <span style="flex: 1;">${param.seriesName}:</span>
                <span style="font-weight: 600; margin-left: 16px;">${value}</span>
              </div>
            `;
          });
          return result;
        }
      },
      legend: {
        data: ['Revenue', 'Patients'],
        top: 10,
        textStyle: {
          color: '#374151'
        }
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        top: '15%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: doctorNames,
        axisLine: {
          lineStyle: {
            color: '#e5e7eb'
          }
        },
        axisLabel: {
          color: '#6b7280',
          fontSize: 12,
          interval: 0,
          rotate: doctorNames.length > 3 ? 45 : 0
        }
      },
      yAxis: [
        {
          type: 'value',
          name: 'Revenue (QR)',
          position: 'left',
          axisLine: {
            lineStyle: {
              color: '#e5e7eb'
            }
          },
          axisLabel: {
            color: '#6b7280',
            fontSize: 12,
            formatter: (value) => `${(value / 1000).toFixed(0)}K`
          },
          splitLine: {
            lineStyle: {
              color: '#f3f4f6'
            }
          }
        },
        {
          type: 'value',
          name: 'Patients',
          position: 'right',
          axisLine: {
            lineStyle: {
              color: '#e5e7eb'
            }
          },
          axisLabel: {
            color: '#6b7280',
            fontSize: 12
          },
          splitLine: {
            show: false
          }
        }
      ],
      series: [
        {
          name: 'Revenue',
          type: 'bar',
          data: revenues,
          itemStyle: {
            color: {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: '#dc2626' },
                { offset: 1, color: '#ef4444' }
              ]
            },
            borderRadius: [4, 4, 0, 0]
          },
          emphasis: {
            itemStyle: {
              color: '#b91c1c'
            }
          }
        },
        {
          name: 'Patients',
          type: 'line',
          yAxisIndex: 1,
          data: patients,
          lineStyle: {
            width: 3,
            color: '#2563eb'
          },
          itemStyle: {
            color: '#2563eb',
            borderWidth: 2,
            borderColor: '#ffffff'
          },
          symbol: 'circle',
          symbolSize: 8,
          emphasis: {
            focus: 'series'
          }
        }
      ]
    };

    chartInstance.current.setOption(option, true);

    // Handle resize
    const handleResize = () => {
      if (chartInstance.current) {
        chartInstance.current.resize();
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [data]);

  useEffect(() => {
    return () => {
      if (chartInstance.current) {
        chartInstance.current.dispose();
        chartInstance.current = null;
      }
    };
  }, []);

  return (
    <div 
      ref={chartRef} 
      className="w-full h-full"
      style={{ minHeight: '250px' }}
    />
  );
};

export default DoctorPerformanceChart;